import React, { useMemo } from "react";
import { areaY, colorLegend, defineChart, lineY } from "@tanstack/charts";
import { decorative } from "@tanstack/charts/mark/decorative";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { scaleUtc } from "d3-scale";
import { tooltip } from "@tanstack/charts/tooltip";

import { ChartFrame } from "./ChartFrame";
import { COLORWAY } from "./chartTheme";
import { WaterfallChart, WaterfallItem } from "./WaterfallChart";

function oneDecimal(value: number): string {
    return value.toFixed(1);
}

export function AttributionPlot({
    traces,
    yLabel,
    height = 320,
    width,
    style,
    onReady
}: {
    traces: any[];
    yLabel?: string;
    height?: number;
    width?: number;
    style?: React.CSSProperties;
    onReady?: () => void;
}): React.ReactElement {
    const first = traces?.[0];
    const definition = useMemo(() => {
        if (!first || first.type === "waterfall") return null;
        const layers = traces.filter((trace) => trace.fill);
        const total = traces.find((trace) => trace.name === "Total" && !trace.fill);
        const rows: { date: Date; series: string; y1: number; y2: number }[] = [];
        if (layers.length > 0 && Array.isArray(layers[0].x)) {
            for (let i = 0; i < layers[0].x.length; i++) {
                let previous = 0;
                for (const layer of layers) {
                    const value = Number(layer.y?.[i] ?? 0);
                    rows.push({
                        date: new Date(layer.x[i]),
                        series: String(layer.name),
                        y1: previous,
                        y2: value
                    });
                    previous = value;
                }
            }
        }
        const totalRows = total
            ? (total.x as unknown[]).map((date, index) => ({
                  date: new Date(date as string),
                  value: Number(total.y?.[index] ?? 0),
                  series: "Total"
              }))
            : [];
        const names = layers.map((layer) => String(layer.name));
        return defineChart({
            marks: [
                decorative(
                    areaY(rows, {
                        x: "date",
                        y1: "y1",
                        y2: "y2",
                        z: "series",
                        color: "series",
                        fillOpacity: 0.75
                    })
                ),
                lineY(totalRows, {
                    x: "date",
                    y: "value",
                    stroke: "#111827",
                    strokeWidth: 2
                })
            ],
            scales: {
                x: { scale: scaleUtc, axis: { label: "Date" } },
                y: {
                    scale: scaleLinear,
                    nice: true,
                    grid: true,
                    axis: { label: yLabel, ticks: { format: oneDecimal } }
                }
            },
            color: {
                domain: names,
                range: names.map((_, index) => COLORWAY[index % COLORWAY.length]),
                legend: colorLegend()
            },
            tooltip
        });
    }, [first, traces, yLabel]);

    if (!first) return <div />;

    if (first.type === "waterfall") {
        const items: WaterfallItem[] = (first.x as string[]).map((name, index) => ({
            name: String(name),
            value: Number(first.y?.[index] ?? 0),
            measure: first.measure?.[index] === "total" ? "total" : "relative",
            text: first.text?.[index]
        }));
        return (
            <WaterfallChart
                items={items}
                colors={{
                    increase: first.increasing?.marker?.color,
                    decrease: first.decreasing?.marker?.color,
                    total: first.totals?.marker?.color
                }}
                yFormat={oneDecimal}
                yLabel={yLabel}
                ariaLabel={yLabel || "Attribution"}
                height={height}
                width={width}
                style={style}
                onReady={onReady}
            />
        );
    }

    return (
        <ChartFrame
            definition={definition}
            ariaLabel={yLabel || "Attribution"}
            height={height}
            width={width}
            style={style}
            onReady={onReady}
        />
    );
}
